import { useEffect, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { SkeletonContentLines } from "../FileRecordCard/FileRecordCardSkeleton";

/**
 * Cover for an HTML card whose live document hasn't painted yet: the
 * server-rendered preview JPEG when one is usable, else the loading
 * skeleton. Shared by the block card and the legacy thumb card so their
 * loading states cannot drift.
 *
 * The no-preview case wears FileRecordCardSkeleton's shapes rather than a
 * file glyph: a node reaches this card straight from that skeleton, so
 * anything else pops a second, unrelated loading state into the ~200ms the
 * document takes to fetch and parse. The shapes only, not the sweep — the
 * share view mounts these by the dozen behind the admission scheduler, and
 * they are up for a fraction of a second each.
 *
 * - Not `loading="lazy"`. A block shows its poster until the scheduler gets
 *   round to its document, which on a large canvas is a while, and a
 *   deferred fetch means the grey plate is what the reader pans onto. The
 *   picture is the resting state, so it is fetched like one.
 * - `onError` demotes a dead preview URL (expired or pruned object) to the
 *   skeleton instead of leaving an invisible broken `<img>` up for the whole
 *   progressive-mount wait.
 * - `pointer-events-none` throughout: the placeholder overlays the shield and
 *   the Open affordance and must never swallow their clicks.
 * - `showPlate` marks surfaces where an opaque plate is acceptable. Frameless
 *   blocks pass false so an absent/failed preview degrades to nothing — an
 *   opaque box under a chromeless block reads as a bug.
 * - The poster is cropped by the wrapper's overflow, not by `object-fit`
 *   (`min-h-full` + `object-cover` only bite for a poster wider than the
 *   card). Same picture, but a fitted canvas paints every poster at once, and
 *   300 posters cropped by `object-fit` held ~800 MB of Chromium shared
 *   memory in the screenshot lambda, against ~33 MB cropped by the wrapper.
 * - `capture` (the screenshot page): the first `CAPTURE_POSTER_BUDGET`
 *   posters are shown, the rest fall back to the plate with the `title`, and
 *   at most `CAPTURE_POSTER_LOADS` load at once — every response in flight
 *   holds a ~2 MiB shared-memory buffer in the lambda's /tmp. A poster
 *   not yet decoded (a loaded but undecoded image is screenshotted as the
 *   bare plate), and a card whose live document the capture is
 *   still waiting for (`pending`), carry `data-capture-pending`.
 */
export function HtmlCardPlaceholder({
  previewImageUrl,
  showPlate,
  capture = false,
  pending = false,
  title,
}: {
  previewImageUrl: string | null;
  showPlate: boolean;
  capture?: boolean;
  pending?: boolean;
  title?: string;
}) {
  const [previewFailed, setPreviewFailed] = useState(false);
  // Taken once, when the card first has a URL: the URL arrives with the file
  // record, after the first render.
  const inBudgetRef = useRef<boolean | null>(null);
  if (capture && previewImageUrl && inBudgetRef.current === null) {
    inBudgetRef.current = takeCapturePoster();
  }
  const showPoster =
    !!previewImageUrl && !previewFailed && (!capture || !!inBudgetRef.current);

  const [loadGranted, setLoadGranted] = useState(!capture);
  const [decoded, setDecoded] = useState(!capture);
  const releaseLoadRef = useRef<(() => void) | null>(null);
  const waitsForLoad = capture && showPoster;
  useEffect(() => {
    if (!waitsForLoad) return;
    const release = acquirePosterLoad(() => setLoadGranted(true));
    releaseLoadRef.current = release;
    return () => {
      releaseLoadRef.current = null;
      release();
    };
  }, [waitsForLoad]);
  const releaseLoad = () => {
    releaseLoadRef.current?.();
    releaseLoadRef.current = null;
  };

  const pendingAttr =
    pending || (showPoster && !decoded) ? { "data-capture-pending": "" } : {};

  if (showPoster) {
    return (
      <div
        {...pendingAttr}
        className={cn(
          "pointer-events-none absolute inset-0 z-10 overflow-hidden",
          // Framed cards keep the plate under the image so a still-decoding
          // JPEG isn't a transparent hole.
          showPlate && "bg-muted",
        )}
      >
        <img
          data-html-card-placeholder=""
          src={loadGranted ? previewImageUrl : undefined}
          alt=""
          aria-hidden
          draggable={false}
          decoding={capture ? "sync" : "async"}
          onLoad={() => {
            releaseLoad();
            setDecoded(true);
          }}
          onError={() => {
            releaseLoad();
            setDecoded(true);
            setPreviewFailed(true);
          }}
          className="absolute inset-x-0 top-0 h-auto min-h-full w-full object-cover object-top"
        />
      </div>
    );
  }
  if (!showPlate) return null;
  return (
    <div
      {...pendingAttr}
      data-html-card-placeholder=""
      className="pointer-events-none absolute inset-0 z-10 flex flex-col overflow-hidden bg-card opacity-75"
    >
      {capture && title && (
        <p className="truncate px-4 pt-3 text-sm font-medium text-muted-foreground">
          {title}
        </p>
      )}
      <SkeletonContentLines />
    </div>
  );
}

/** Posters one capture page shows, and loads at once (see `capture`). */
export const CAPTURE_POSTER_BUDGET = 300;
export const CAPTURE_POSTER_LOADS = 16;

let capturePostersLeft = CAPTURE_POSTER_BUDGET;
function takeCapturePoster(): boolean {
  if (capturePostersLeft <= 0) return false;
  capturePostersLeft--;
  return true;
}

let posterLoadsInFlight = 0;
const posterLoadQueue: (() => void)[] = [];
/** Calls `start` once a load slot is free; the returned release frees it
 *  (or leaves the queue) and is idempotent. */
function acquirePosterLoad(start: () => void): () => void {
  let state: "queued" | "loading" | "done" = "queued";
  const grant = () => {
    state = "loading";
    posterLoadsInFlight++;
    start();
  };
  if (posterLoadsInFlight < CAPTURE_POSTER_LOADS) grant();
  else posterLoadQueue.push(grant);
  return () => {
    if (state === "queued") {
      posterLoadQueue.splice(posterLoadQueue.indexOf(grant), 1);
    } else if (state === "loading") {
      posterLoadsInFlight--;
      posterLoadQueue.shift()?.();
    }
    state = "done";
  };
}
